import { SelectionModel } from '@angular/cdk/collections';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import * as _ from 'lodash';

import { CuttingMatBaseGroup } from '../../models/cutting/cutting-material-group.model';

@Component({
  selector: 'csl-group-matrial-tree',
  templateUrl: './cutting-group-material-tree.component.html',
  styleUrls: ['./cutting-group-material-tree.component.scss'],
})
export class GroupMaterialTreeComponent {
  @Input() set groupMaterials(groups: CuttingMatBaseGroup[]) {
    this.dataSource.data = _.cloneDeep(groups);

    this.levels.clear();
    for (const node of groups) {
      this.loadLevel(node, 0);
    }

    const rootNode = this.dataSource?.data[0]?.id === 0 ? this.dataSource?.data[0] : null;
    if (rootNode) {
      this.treeControl.expand(rootNode);
    }
  }

  @Input() set focusedGroupId(groupId: number) {
    if (groupId !== -1) {
      const findNode = this.dataSource.data.find(item => item.id === groupId);
      if (findNode) {
        this.onFocusNode(findNode);
      }
    }
  }

  @Input() multiSelect = false;

  @Output() selectedGroupChange: EventEmitter<CuttingMatBaseGroup> = new EventEmitter();

  set selectedGroupIdList(groups: number[]) {
    this.checklistSelection.clear();
    this.checklistSelection.select(...groups);
  }

  get selectedGroupIdList(): number[] {
    if (this.multiSelect) {
      return this.checklistSelection.selected;
    } else {
      return new Array(this.activeNode.id);
    }
  }

  treeControl = new NestedTreeControl<CuttingMatBaseGroup>(node => node.children);
  dataSource = new MatTreeNestedDataSource<CuttingMatBaseGroup>();
  activeNode: CuttingMatBaseGroup;
  levels: Map<CuttingMatBaseGroup, number> = new Map<CuttingMatBaseGroup, number>();
  checklistSelection = new SelectionModel<number>(true /* multiple */);

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: CuttingMatBaseGroup): void {
    let parent: CuttingMatBaseGroup | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /**
   * Check root node checked state and change it accordingly
   * @param node
   */
  checkRootNodeSelection(node: CuttingMatBaseGroup): void {
    const nodeSelected = this.checklistSelection.isSelected(node.id);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every(child => this.checklistSelection.isSelected(child.id));
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node.id);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node.id);
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: CuttingMatBaseGroup): CuttingMatBaseGroup | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.dataSource.data.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.dataSource.data[i];

      if (this.getLevel(node) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  nodeItemSelectionToggle(node: CuttingMatBaseGroup): void {
    this.checklistSelection.toggle(node.id);
    const descendants = this.treeControl.getDescendants(node);
    const buffer: number[] = [];
    for (const item of descendants) {
      buffer.push(item.id);
    }
    this.checklistSelection.isSelected(node.id)
      ? this.checklistSelection.select(...buffer)
      : this.checklistSelection.deselect(...buffer);

    // Force update for the parent
    descendants.every(child => this.checklistSelection.isSelected(child.id));
    this.checkAllParentsSelection(node);
  }

  getLevel(node: CuttingMatBaseGroup): number {
    return this.levels.get(node);
  }

  hasChild = (n: number, node: CuttingMatBaseGroup): boolean => !!node.children && node.children.length > 0;

  loadLevel(item: CuttingMatBaseGroup, level: number): void {
    this.levels.set(item, level);

    if (item.children && item.children.length > 0) {
      for (const childItem of item.children) {
        this.loadLevel(childItem, level + 1);
      }
    }
  }

  onFocusNode(node: CuttingMatBaseGroup): void {
    this.activeNode = node;

    this.selectedGroupChange.emit(node);
  }

  getNodeContext(node: CuttingMatBaseGroup): CuttingMatBaseGroup {
    return node.id === 0 ? { ...node, name: $localize`Общее` } : node;
  }
}
